/**
 * Locale/Currency/Country utility — mirrors utils/branch.ts's pattern (localStorage cache +
 * a CustomEvent for reactive updates) rather than a heavier React Context, so currency/country
 * settings can be read from anywhere — including non-component code like print templates and
 * PDF generators — without every consumer needing a Provider wrapper.
 *
 * Source of truth: GET/PUT /settings/localization (backend/src/settings) — the one real,
 * editable "country / language / currency / timezone / tax scheme" record for this
 * deployment. loadLocaleFromServer() is called once on app boot (see App.tsx) and caches the
 * result here; everything else reads the synchronous cache so currency formatting doesn't need
 * to be async everywhere a number is shown.
 *
 * Language SWITCHING itself is handled separately by i18n/index.ts (i18next) — that's a purely
 * client-side, per-user preference stored under its own localStorage key (i18next's own
 * 'i18nextLng'), so a user's chosen UI language works even before this deployment's
 * localization settings have loaded (e.g. on the Login screen). `languageCode` here is only
 * the deployment-wide DEFAULT that seeds a first-time visitor's language, not an override for
 * someone who already picked one.
 */
import api from '../services/api';

export interface LocaleSettings {
  country: string;
  languageCode: string;
  supportedLanguages: string[];
  currencyCode: string;
  currencySymbol: string;
  numberFormat: string;
  taxScheme: string;
  taxLabel: string;
  timezone: string;
  dateFormat: string;
  timeFormat: string;
}

export const DEFAULT_LOCALE: LocaleSettings = {
  country: 'Kenya', languageCode: 'en', supportedLanguages: ['en'],
  currencyCode: 'KES', currencySymbol: 'KSh', numberFormat: 'en-KE',
  taxScheme: 'VAT', taxLabel: 'VAT', timezone: 'Africa/Nairobi', dateFormat: 'DD/MM/YYYY', timeFormat: '24h',
};

function readCache(): LocaleSettings {
  try {
    const raw = localStorage.getItem('localeSettings');
    return raw ? { ...DEFAULT_LOCALE, ...JSON.parse(raw) } : { ...DEFAULT_LOCALE };
  } catch { return { ...DEFAULT_LOCALE }; }
}

let cached: LocaleSettings = readCache();

function writeCache(loc: LocaleSettings) {
  cached = loc;
  try { localStorage.setItem('localeSettings', JSON.stringify(loc)); } catch { /* private-mode storage may be unavailable */ }
  window.dispatchEvent(new CustomEvent('localeChanged', { detail: loc }));
}

export function getLocale(): LocaleSettings { return cached; }

/** Called once on app boot (App.tsx) — pulls this deployment's real settings from the backend
 *  and overwrites the cached defaults/last-known-good copy. Fails silently (keeps the cached
 *  copy) if unreachable — e.g. not logged in yet, offline, or the localization migration
 *  hasn't run against this DB — so callers never need to special-case a missing settings row. */
export async function loadLocaleFromServer(): Promise<LocaleSettings> {
  try {
    const r = await (api as any).getLocalization?.();
    const data = r?.data;
    if (data && data.currencyCode) {
      const merged: LocaleSettings = { ...DEFAULT_LOCALE, ...data };
      writeCache(merged);
      return merged;
    }
  } catch { /* keep cached defaults */ }
  return cached;
}

export async function saveLocaleToServer(patch: Partial<LocaleSettings>): Promise<LocaleSettings> {
  const merged = { ...cached, ...patch };
  const r = await (api as any).updateLocalization?.(merged);
  const saved: LocaleSettings = { ...merged, ...(r?.data || {}) };
  writeCache(saved);
  return saved;
}

/** Formats a number as this deployment's configured currency. Intl.NumberFormat gets the
 *  thousands separators/decimal places right per locale; falls back to a plain symbol + fixed
 *  2dp if the browser's Intl implementation doesn't recognise numberFormat/currencyCode (this
 *  happens for a few less-common African currency codes on older engines). */
export function formatCurrency(amount: number | string | null | undefined, opts?: { withSymbol?: boolean }): string {
  const n = typeof amount === 'string' ? parseFloat(amount) : (amount ?? 0);
  const value = isNaN(n as number) ? 0 : (n as number);
  const { currencyCode, currencySymbol, numberFormat } = cached;
  try {
    return new Intl.NumberFormat(numberFormat || 'en-KE', {
      style: 'currency', currency: currencyCode || 'KES', currencyDisplay: 'narrowSymbol',
    }).format(value);
  } catch {
    const formatted = value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return opts?.withSymbol === false ? formatted : `${currencySymbol || currencyCode || 'KES'} ${formatted}`;
  }
}

/** Bare number, no currency symbol — for table columns that already show a currency header. */
export function formatAmount(amount: number | string | null | undefined): string {
  return formatCurrency(amount, { withSymbol: false });
}

export function getCurrencyCode(): string { return cached.currencyCode; }
export function getCurrencySymbol(): string { return cached.currencySymbol; }
export function getTaxLabel(): string { return cached.taxLabel || 'Tax'; }
export function getCountry(): string { return cached.country; }
